import React, { useMemo } from 'react'
import { CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react-pro'
import { CChart } from '@coreui/react-chartjs'
import { useChartTheme, baseChartOptions, PLATFORM_META } from '../AnalyticsV2/chartTheme'
import {
  ChartSkeleton,
  EmptyState,
  ErrorState,
  formatDecimal,
  formatNumber,
} from '../AnalyticsV2/common'
import RankedBarList from '../AnalyticsV2/RankedBarList'
import { WEEKDAYS, gameTypeLabel, hourLabel } from './platformHelpers'

const Section = ({
  title,
  subtitle,
  loading,
  error,
  onRetry,
  empty,
  height = 260,
  children,
  plain,
}) => (
  <CCard className="h-100">
    <CCardHeader>
      <strong>{title}</strong>
      {subtitle && <div className="small text-medium-emphasis">{subtitle}</div>}
    </CCardHeader>
    <CCardBody className={plain ? 'pa-ranked-plain' : undefined}>
      {loading && <ChartSkeleton height={height} />}
      {!loading && error && <ErrorState message={error} onRetry={onRetry} height={height} />}
      {!loading && !error && empty && <EmptyState height={height} />}
      {!loading && !error && !empty && children}
    </CCardBody>
  </CCard>
)

/**
 * Weekday × hour, one hue light→dark (magnitude is sequential). Built from
 * HTML cells rather than a canvas so each cell is its own hover target.
 */
const Heatmap = ({ grid }) => {
  const max = useMemo(() => Math.max(0, ...grid.flat()), [grid])

  const peak = useMemo(() => {
    let best = { wd: 0, hr: 0, v: -1 }
    grid.forEach((row, wd) =>
      row.forEach((v, hr) => {
        if (v > best.v) best = { wd, hr, v }
      }),
    )
    return best
  }, [grid])

  const busiestDay = useMemo(() => {
    const totals = grid.map((row) => row.reduce((s, v) => s + v, 0))
    const i = totals.indexOf(Math.max(...totals))
    return WEEKDAYS[i]
  }, [grid])

  const level = (v) => (max > 0 && v > 0 ? 0.12 + (v / max) * 0.88 : 0)

  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <span className="av2-chip">
          Peak hour:{' '}
          <strong>
            {WEEKDAYS[peak.wd]} {hourLabel(peak.hr)}
          </strong>{' '}
          ({formatNumber(peak.v)} sessions)
        </span>
        <span className="av2-chip">
          Busiest day: <strong>{busiestDay}</strong>
        </span>
      </div>

      <div className="av2-scroll-x">
        <div className="pa-heatmap" role="table" aria-label="Sessions by weekday and hour">
          <div className="pa-heatmap-row" role="row">
            <span className="pa-heatmap-label" />
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} className="pa-heatmap-hour" role="columnheader">
                {h % 3 === 0 ? hourLabel(h) : ''}
              </span>
            ))}
          </div>
          {grid.map((row, wd) => (
            <div key={wd} className="pa-heatmap-row" role="row">
              <span className="pa-heatmap-label" role="rowheader">
                {WEEKDAYS[wd]}
              </span>
              {row.map((v, hr) => (
                <span
                  key={hr}
                  role="cell"
                  className="pa-heatmap-cell"
                  style={{ '--pa-level': level(v) }}
                  title={`${WEEKDAYS[wd]} ${hourLabel(hr)}–${hourLabel(
                    (hr + 1) % 24,
                  )}: ${formatNumber(v)} sessions`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 justify-content-end small text-medium-emphasis mt-2">
        Fewer
        {[0.12, 0.34, 0.56, 0.78, 1].map((l) => (
          <span key={l} className="pa-heatmap-cell pa-heatmap-key" style={{ '--pa-level': l }} />
        ))}
        More
      </div>
    </>
  )
}

const PlatformDonut = ({ rows, theme }) => {
  const meta = (key) => PLATFORM_META.find((p) => p.key === key)
  const colorFor = (key) => (meta(key) ? theme.series[meta(key).slot] : theme.neutral)
  const labelFor = (key) => (meta(key) ? meta(key).label : 'Unknown device')

  return (
    <>
      <div className="av2-chart av2-chart--donut">
        <CChart
          customTooltips={false}
          type="doughnut"
          data={{
            labels: rows.map((r) => labelFor(r.device_type)),
            datasets: [
              {
                data: rows.map((r) => r.sessions),
                backgroundColor: rows.map((r) => colorFor(r.device_type)),
                borderColor: theme.surface,
                borderWidth: 2,
                hoverBorderColor: theme.surface,
              },
            ],
          }}
          options={baseChartOptions(theme, {
            cutout: '62%',
            plugins: { legend: { display: false } },
          })}
        />
      </div>
      <ul className="pa-legend mt-3">
        {rows.map((r) => (
          <li key={r.device_type}>
            <span className="av2-swatch" style={{ background: colorFor(r.device_type) }} />
            <span className="me-auto">{labelFor(r.device_type)}</span>
            <span className="fw-semibold">{formatNumber(r.users)} users</span>
            <span className="text-medium-emphasis pa-legend-pct">
              {formatDecimal(r.share_pct, 0)}%
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

const UsagePanel = ({ usage, onRetry, onOpenScreen }) => {
  const theme = useChartTheme()
  const d = usage.data || {}
  const common = { loading: usage.loading, error: usage.error, onRetry }

  const heatEmpty = !d.heatmap || d.heatmap.flat().every((v) => !v)
  const platforms = (d.platforms || []).filter((r) => r.sessions > 0)

  return (
    <>
      <CRow className="g-3 mb-3">
        <CCol xs={12}>
          <Section
            {...common}
            title="When people use the app"
            subtitle="Sessions by day of week and hour, on your clock"
            empty={heatEmpty}
            height={240}
          >
            {!heatEmpty && <Heatmap grid={d.heatmap} />}
          </Section>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol xs={12} lg={4}>
          <Section
            {...common}
            title="iPhone vs Android"
            subtitle="Share of sessions"
            empty={!platforms.length}
          >
            <PlatformDonut rows={platforms} theme={theme} />
          </Section>
        </CCol>
        <CCol xs={12} lg={4}>
          <Section
            {...common}
            plain
            title="App versions in use"
            subtitle="Newest first — has the latest build rolled out?"
            empty={!(d.versions || []).length}
          >
            <RankedBarList
              rows={(d.versions || []).map((v) => ({
                key: v.app_version,
                label: `v${v.app_version}`,
                value: v.users,
                share: v.share_pct,
              }))}
              color={theme.series[2]}
              valueLabel="users"
            />
          </Section>
        </CCol>
        <CCol xs={12} lg={4}>
          <Section
            {...common}
            plain
            title="Game types"
            subtitle="Active users by kind of game"
            empty={!(d.game_types || []).length}
          >
            <RankedBarList
              rows={(d.game_types || []).map((t) => ({
                key: t.game_type,
                label: gameTypeLabel(t.game_type),
                sublabel: `${formatNumber(t.active_games)} of ${formatNumber(
                  t.games,
                )} games active`,
                value: t.users,
              }))}
              color={theme.series[3]}
              valueLabel="users"
            />
          </Section>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol xs={12} lg={6}>
          <Section
            {...common}
            title="Most viewed screens"
            subtitle="Across every game — click a screen to see which games use it"
            empty={!(d.top_screens || []).length}
          >
            <RankedBarList
              rows={(d.top_screens || []).map((s) => ({
                key: s.page_name,
                label: s.page_name,
                value: s.views,
                people: s.users,
              }))}
              color={theme.series[0]}
              valueLabel="views"
              onRowClick={(row) => onOpenScreen(row.key)}
            />
          </Section>
        </CCol>
        <CCol xs={12} lg={6}>
          <Section
            {...common}
            title="Top actions"
            subtitle="Taps, shares and follows across every game"
            empty={!(d.top_actions || []).length}
          >
            <RankedBarList
              rows={(d.top_actions || []).map((a) => ({
                key: a.event_name,
                label: a.event_name,
                value: a.count,
                people: a.users,
              }))}
              color={theme.series[1]}
              valueLabel="times"
            />
          </Section>
        </CCol>
      </CRow>
    </>
  )
}

export default UsagePanel
